import React, { useMemo } from "react";
import { Card, Tag } from "antd";
import dayjs from "dayjs";

const glassStyle = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.35)",
  borderRadius: 16,
  boxShadow:
    "0 10px 30px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
};

//////////////////////////////////////////////////////
// 🤖 AI ENGINE (rule-based for now, AI-ready later)
//////////////////////////////////////////////////////

const buildInsights = ({ summary, expiringUsers, trend }) => {
  const insights = [];

  // ---------------- Expiry Risk ----------------
  const highRisk = expiringUsers?.length || 0;

  if (highRisk > 0) {
    insights.push({
      type: "warning",
      text: `${highRisk} users are expiring soon`,
      tag: "Churn Risk",
    });
  }

  // ---------------- Growth Signal ----------------
  if (Array.isArray(trend) && trend.length >= 2) {
    const latest = trend[trend.length - 1]?.count || 0;
    const prev = trend[trend.length - 2]?.count || 0;

    if (latest > prev) {
      insights.push({
        type: "success",
        text: "Subscriptions are trending upward",
        tag: "Growth",
      });
    } else if (latest < prev) {
      insights.push({
        type: "danger",
        text: "Subscription momentum slowing",
        tag: "Attention",
      });
    }
  }

  // ---------------- User Activity ----------------
  if (summary?.active_users && summary?.total_users) {
    const ratio =
      summary.total_users > 0
        ? (summary.active_users / summary.total_users) * 100
        : 0;

    if (ratio < 60) {
      insights.push({
        type: "warning",
        text: "Active user ratio is below optimal",
        tag: "Engagement",
      });
    }
  }

  // fallback
  if (insights.length === 0) {
    insights.push({
      type: "info",
      text: "System metrics look healthy",
      tag: "Stable",
    });
  }

  return insights.slice(0, 4);
};

//////////////////////////////////////////////////////
// 🎨 Tag color mapper
//////////////////////////////////////////////////////

const tagColor = (type) => {
  switch (type) {
    case "success":
      return "green";
    case "warning":
      return "orange";
    case "danger":
      return "red";
    default:
      return "blue";
  }
};

//////////////////////////////////////////////////////
// 🚀 COMPONENT
//////////////////////////////////////////////////////

const AiInsightPanel = ({ summary, expiringUsers, trend }) => {
  const insights = useMemo(
    () => buildInsights({ summary, expiringUsers, trend }),
    [summary, expiringUsers, trend],
  );

  return (
    <Card title="🤖 AI Insights" style={glassStyle} bodyStyle={{ padding: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {insights.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.45)",
              transition: "all 0.25s ease",
              cursor: "default",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <span style={{ fontWeight: 500 }}>{item.text}</span>
            <Tag color={tagColor(item.type)}>{item.tag}</Tag>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default AiInsightPanel;
